import { useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties, RefObject } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { Button } from '#/shared/ui/button'
import { InsightIcon } from '#/shared/ui/icons'

import type { MarketRateStats } from '../model/types'
import { useSuggestedPriceSd } from '../model/use-suggested-price-sd'
import { MarketRateStatsBadge } from './market-rate-stats-badge'

const GAP_PX = 6
const MARGIN_PX = 8

interface MarketRateCheckButtonProps {
  shipmentId: string | undefined
  /** Seed the recommended price into the tariff input (no persist). */
  onUsePrice?: (price: number) => void
}

function CheckPanel({
  stats,
  isFetching,
  errorMessage,
  onUse,
}: {
  stats: MarketRateStats | undefined
  isFetching: boolean
  errorMessage: string | undefined
  onUse?: (price: number) => void
}) {
  const { t } = useTranslation()
  if (stats) {
    return (
      <div className="flex items-center gap-2.5">
        <MarketRateStatsBadge stats={stats} />
        {onUse && (
          <Button
            type="button"
            variant="outline"
            size="xxs"
            pressEffect
            onClick={() => onUse(stats.recommended)}
          >
            {t('table.marketRate.use')}
          </Button>
        )}
      </div>
    )
  }
  if (isFetching) {
    return (
      <span className="flex items-center gap-1.25 text-2xs text-gray-500">
        <span className="size-3.5 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
        {t('orderDetails.payment.checkingRate')}
      </span>
    )
  }
  return (
    <span className="text-2xs text-gray-500">
      {errorMessage ?? t('table.marketRate.empty')}
    </span>
  )
}

/**
 * Anchor the card's RIGHT edge just left of the trigger and let it grow
 * leftward, vertically centered via a transform. Pinning the right edge (rather
 * than a width-derived left) keeps the card put when its content resizes — e.g.
 * the spinner → resolved badge swap — so it never drifts onto the icon.
 */
function useLeftAnchorStyle(
  open: boolean,
  triggerRef: RefObject<HTMLButtonElement | null>,
): CSSProperties {
  const [style, setStyle] = useState<CSSProperties>({
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 80,
    visibility: 'hidden',
  })
  useLayoutEffect(() => {
    if (!open) return
    const measure = () => {
      const trigger = triggerRef.current
      if (!trigger) return
      const r = trigger.getBoundingClientRect()
      setStyle({
        position: 'fixed',
        top: r.top + r.height / 2,
        right: Math.max(MARGIN_PX, window.innerWidth - r.left + GAP_PX),
        transform: 'translateY(-50%)',
        zIndex: 80,
      })
    }
    measure()
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    return () => {
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [open, triggerRef])
  return style
}

/**
 * Super Dispatch rate check for the leads tariff cell. An insight-icon button
 * opens a floating card (Figma 11064-292136) to the left of the icon with the
 * recommended price, per-mile rate, and confidence — fetched lazily on open.
 */
export function MarketRateCheckButton({
  shipmentId,
  onUsePrice,
}: MarketRateCheckButtonProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const { stats, isFetching, errorMessage } = useSuggestedPriceSd({
    shipmentId,
    enabled: open,
  })
  const cardStyle = useLeftAnchorStyle(open, triggerRef)

  const handleUse = onUsePrice
    ? (price: number) => {
        onUsePrice(price)
        setOpen(false)
      }
    : undefined

  if (shipmentId === undefined) return null

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        aria-label={t('orderDetails.payment.checkRate')}
        onClick={(e) => {
          e.stopPropagation()
          setOpen((v) => !v)
        }}
        className="flex size-5 cursor-pointer items-center justify-center rounded hover:bg-gray-100"
      >
        <InsightIcon className="size-3.5 text-orange-500" />
      </button>
      {open &&
        createPortal(
          <>
            <div
              className="fixed inset-0"
              style={{ zIndex: 80 }}
              onClick={() => setOpen(false)}
            />
            <div
              style={cardStyle}
              className="rounded-lg bg-white px-2.5 py-2 shadow-popover"
            >
              <CheckPanel
                stats={stats}
                isFetching={isFetching}
                errorMessage={errorMessage}
                onUse={handleUse}
              />
            </div>
          </>,
          document.body,
        )}
    </>
  )
}
